import { Button, Hidden, Paper, Typography, CircularProgress, Alert } from "@mui/material";
import { Box } from "@mui/system";
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApartmentIcon from "@mui/icons-material/Apartment";
import { useDownMedium } from "../../utils/useScreenSize";
import { isAdmin, isKWALLAdmin, isOrgAdmin } from "../../utils";
import { confirmAlert } from "react-confirm-alert";
import {
  LOCAL_STORAGE_CURRENT_ORG,
  LOCAL_STORAGE_CURRENT_USER,
  getLocalStorage,
  setLocalStorage,
} from "../../utils/localStorage";
import "./OrganizationItem.css";
import { useContext } from "react";
import { AuthContext } from "../../components/FirebaseAuth";
import { CloudFunctions } from "../FirebaseAuth/firebase";
import { httpsCallable } from 'firebase/functions';
import SwitchAccountIcon from "@mui/icons-material/SwitchCamera";
import PsychologyIcon from "@mui/icons-material/Psychology";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshOrgProfileButton from './RefreshOrgProfileButton';

export const OrganizationItem = ({ organization, siteCount = 0, onPopulateTrafficData, isPopulatingTraffic }) => {
  const history = useNavigate();
  const isDownMd = useDownMedium();
  const currentUser = getLocalStorage(LOCAL_STORAGE_CURRENT_USER);
  const { authUser } = useContext(AuthContext);
  
  // AI Summary generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState('');
  const [generationError, setGenerationError] = useState('');

  const handleManageOrganization = useCallback(() => {
    setLocalStorage(LOCAL_STORAGE_CURRENT_ORG, organization);
    history(`/organization_list/detail/${organization.id}`);
  }, [organization, history]);

  const handleSelectOrganization = useCallback(() => {
    setLocalStorage(LOCAL_STORAGE_CURRENT_ORG, organization);
    history("/");
  }, [organization, history]);

  const handleManageSites = useCallback(() => {
    setLocalStorage(LOCAL_STORAGE_CURRENT_ORG, organization);
    history(`/organization_list/sites/${organization.id}`);
  }, [organization, history]);

  const handleManageUsers = useCallback(() => {
    setLocalStorage(LOCAL_STORAGE_CURRENT_ORG, organization);
    history(`/organization/${organization.id}/users/`);
  }, [organization, history]);

  const handleGenerateAISummary = useCallback(async () => {
    setIsGenerating(true);
    setGenerationError('');
    setGenerationResult('');

    try {
      const generateSummariesBatch = httpsCallable(CloudFunctions, 'aiSummaryGenerator-generateSummariesBatch');
      const response = await generateSummariesBatch({ orgId: organization.id });
      
      setGenerationResult(response.data.message || 'AI summary generated successfully!');
    } catch (error) {
      console.error('Error generating AI summary:', error);
      setGenerationError(error.message || 'Failed to generate AI summary');
    }

    setIsGenerating(false);
  }, [organization.id]);

  return (
    <Paper className="organizationItem">
      {organization.imageUrl && !isDownMd ? (
        <Box
          component="img"
          alt="Organization Avatar"
          src={organization.imageUrl}
          className={"organizationItem__image"}
        />
      ) : (
        <ApartmentIcon className={"organizationItem__image"} />
      )}
      <Box className={"organizationItem__content istitle"}>
        <Typography className={"organizationItem__title"}>
          {organization.name}
        </Typography>
      </Box>
      <Box className={"organizationItem__content isContent"}>
        {organization.organizationType && (
          <Typography className={"organizationItem__provider"}>
            <span>Organization Type:</span> {organization.organizationType}
          </Typography>
        )}
        {organization.accountType && (
          <Typography className={"organizationItem__provider"}>
            <span>Collection Type:</span> {organization.accountType}
          </Typography>
        )}
        <Typography className={"organizationItem__provider"}>
          <span>Total Collection(s):</span> {organization.accounts?.length}
        </Typography>
        <Typography className={"organizationItem__provider"}>
          <span>Total Sites:</span> {siteCount}
        </Typography>
        <Hidden smDown>
          <Typography className={"organizationItem__description"}>
            {organization.description}
          </Typography>
        </Hidden>
      </Box>
      
      {/* AI Summary Generation Results */}
      {(generationResult || generationError) && (
        <Box sx={{ mt: 2, mx: 2 }}>
          {generationError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {generationError}
            </Alert>
          )}
          {generationResult && (
            <Alert severity="success" sx={{ mb: 1 }}>
              {generationResult}
            </Alert>
          )}
        </Box>
      )}
      
      <Box className={"organizationItem__btnContainer"}>
        <Button
          variant="outlined"
          color="success"
          onClick={() => {
            confirmAlert({
              title: "Switch Organization",
              message: `Are you sure you want to set '${organization.name}' as your active organization?`,
              buttons: [
                {
                  label: "Yes",
                  onClick: handleSelectOrganization,
                  className: "react-confirm-alert-button-delete",
                },
                {
                  label: "No",
                  className: "react-confirm-alert-button-cancel",
                  onClick: () => {},
                },
              ],
            });
          }}
          className={"organizationItem__button"}
          size={isDownMd ? "small" : "small"}
          startIcon={<SwitchAccountIcon />}
          sx={{
            borderWidth: 2,
            color: "common.white",
            backgroundColor: "success.main",
            "&:hover": {
              opacity: 0.8,
            },
          }}
        >
          Switch to this organization
        </Button>
      </Box>
      {isAdmin(currentUser?.role) ||
      isOrgAdmin(currentUser?.role) ||
      isKWALLAdmin(currentUser?.role) ||
      organization.admins?.includes(authUser.user.uid) ? (
        <Box className={"organizationItem__btnContainer"}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleManageOrganization}
            className={"organizationItem__button"}
            size={isDownMd ? "small" : "small"}
          >
            Manage Organization
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleManageSites}
            className={"organizationItem__button"}
            size={isDownMd ? "small" : "small"}
          >
            Manage Sites
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleManageUsers}
            className={"organizationItem__button"}
            size={isDownMd ? "small" : "small"}
          >
            Manage Users
          </Button>
          
          {/* Organization Profile Refresh Button */}
          <RefreshOrgProfileButton 
            organizationId={organization.id}
            currentUser={currentUser}
            size={isDownMd ? "small" : "small"}
          />
          
          {/* AI Summary Generation Button - kwallAdmin only */}
          {isKWALLAdmin(currentUser?.role) && (
            <Button
              variant="outlined"
              color="warning"
              onClick={handleGenerateAISummary}
              disabled={isGenerating}
              className={"organizationItem__button"}
              size={isDownMd ? "small" : "small"}
              startIcon={isGenerating ? <CircularProgress size={16} /> : <PsychologyIcon />}
              sx={{
                borderColor: 'warning.main',
                color: 'warning.main',
                '&:hover': {
                  borderColor: 'warning.dark',
                  backgroundColor: 'warning.light'
                }
              }}
            >
              {isGenerating ? 'Generating...' : 'Generate AI Summary'}
            </Button>
          )}
          
          {/* Traffic Data Population Button - kwallAdmin only */}
          {isKWALLAdmin(currentUser?.role) && onPopulateTrafficData && (
            <Button
              variant="outlined"
              color="info"
              onClick={onPopulateTrafficData}
              disabled={isPopulatingTraffic}
              className={"organizationItem__button"}
              size={isDownMd ? "small" : "small"}
              startIcon={isPopulatingTraffic ? <CircularProgress size={16} /> : <TrendingUpIcon />}
              sx={{
                borderColor: 'info.main',
                color: 'info.main',
                '&:hover': {
                  borderColor: 'info.dark',
                  backgroundColor: 'info.light'
                }
              }}
            >
              {isPopulatingTraffic ? 'Populating...' : 'Populate Traffic Data'}
            </Button>
          )}
        </Box>
      ) : (
        <Box className={"organizationItem__btnContainer"}>
          <Button
            variant="contained"
            color="primary"
            className={"organizationItem__button"}
            size={isDownMd ? "small" : "small"}
            sx={{
              visibility: "hidden",
            }}
          >
            Manage Organization
          </Button>
        </Box>
      )}
    </Paper>
  );
};
